import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Spinner } from "react-bootstrap";
import { FaArrowRight } from "react-icons/fa6";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { handleLoginPopup } from "../../redux/slice/userSlice";

const LoginModal = ({ open, onClose }) => {
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [userInput, setUserInput] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [newuserInfo, setNewuserInfo] = useState({
    phoneNumber: "",
    name: "",
    email: "",
    city: "",
    state: "",
  });
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const inputRefs = useRef([]);
  const toastRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLogin = Cookies.get("token");

  const showToast = (message, type = "success") => {
    if (!toastRef.current) {
      toastRef.current =
        type === "success"
          ? toast.success(message, {
              onClose: () => {
                toastRef.current = null;
              },
            })
          : toast.error(message, {
              onClose: () => {
                toastRef.current = null;
              },
            });
    }
  };

  const handleClose = () => {
    onClose();
    setShowOtpInput(false);
    setUserInput("");
    setOtp(["", "", "", ""]);
    setIsOtpSent(false);
  };

  const handleBackspace = (index, event) => {
    if (event.keyCode === 8 && index > 0 && otp[index] === "") {
      inputRefs.current[index - 1].focus();
    }
  };

  const verifyOtpHandle = async (e) => {
    try {
      setIsLoading(true);
      e.preventDefault();
      const combinedOtp = otp.join("");
      if (combinedOtp.length !== 4) {
        toast.error("Enter a valid OTP");
        setIsLoading(false);
        return;
      }

      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/verifyOtp",
        { phoneNumber: userInput, otp: combinedOtp },
      );
      setIsLoading(false);
      if (response?.data?.status === "success") {
        const newUser = response?.data?.data?.newUser;
        if (newUser) {
          setShowRegisterModal(true);
          setShowOtpInput(false);
          dispatch(handleLoginPopup(false));
          return; // 🔥 important
        } else {
          onClose();
          Cookies.set("user", response?.data?.data?.user?.name, { expires: 1 });
          Cookies.set("token", response?.data?.data?.token, { expires: 1 });
          Cookies.set("userId", response?.data?.data?.user?._id, {
            expires: 1,
          });
          toast.success(response?.data?.message);
          window.location.reload();
        }
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("Something went wrong!");
    }
  };

  const handlePhoneNumberChange = (e) => {
    const inputPhoneNumber = e.target.value.replace(/\D/g, "");
    if (inputPhoneNumber.length <= 10) {
      setUserInput(inputPhoneNumber);
      setNewuserInfo({ ...newuserInfo, phoneNumber: inputPhoneNumber });
    }
  };

  const addUser = async () => {
    const { name, email, city, state } = newuserInfo;
    if (!name || !email || !city || !state) {
      toast.error("Please enter all fields");
      return;
    }
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/registration",
        newuserInfo,
      );
      if (response.status === 200) {
        setShowRegisterModal(false);
        onClose();
        setShowOtpInput(false);
        Cookies.set("user", response?.data?.data?.user?.name, { expires: 1 });
        Cookies.set("token", response?.data?.data?.token, { expires: 1 });
        Cookies.set("userId", response?.data?.data?.user?._id, { expires: 1 });
        toast.success(response?.data?.message);
        setUserInput("");
        window.location.reload();
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    }
  };

  const isValidPhoneNumber = (phoneNumber) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handleChange = (index, event) => {
    const newOtp = [...otp];
    newOtp[index] = event.target.value.slice(0, 1);
    setOtp(newOtp);
    if (event.target.value !== "" && index < otp.length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleContinue = useCallback(
    async (e, type = 1, googleToken = null) => {
      e.preventDefault();
      try {
        setIsLoading(true);

        if (googleToken) {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/login`,
            { token: googleToken },
          );
          setIsLoading(false);

          if (response?.data?.success) {
            localStorage.setItem("authToken", response?.data?.token);
            localStorage.setItem("user", JSON.stringify(response?.data?.user));
            navigate("/home");
          } else {
            showToast(response?.data?.message, "error");
          }
        } else {
          if (isValidPhoneNumber(userInput)) {
            const response = await axios.post(
              `${process.env.REACT_APP_API_URL}/login`,
              {
                phoneNumber: userInput,
                token: isLogin,
              },
            );
            setIsLoading(false);

            if (response?.data?.status === "success") {
              setShowOtpInput(true);
              if (!isOtpSent) {
                showToast("OTP sent successfully.", "success");
                setIsOtpSent(true);
              }
            } else {
              showToast(response?.data?.message, "error");
            }
          } else {
            setIsLoading(false);
            showToast(
              "Invalid phone number! Please enter a valid 10-digit number.",
              "error",
            );
          }
        }
      } catch (error) {
        setIsLoading(false);
        showToast("Something went wrong!", "error");
      }
    },
    [userInput, isOtpSent, isLogin, navigate],
  );

  const handleGoogleLoginSuccess = async (response) => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/login`, {
        token: response.credential,
      });

      if (res?.data?.success) {
        Cookies.set("user", res.data.user.name);
        Cookies.set("token", res.data.token);
        Cookies.set("userId", res.data.user._id);

        toast.success("Login successful");
        onClose();
        window.location.reload();
      }
    } catch {
      toast.error("Google login failed");
    }
  };

  const handleGoogleLoginFailure = () => {
    toast.error("Google login failed");
  };

  return (
    <>
      {/* LOGIN MODAL */}
      <Modal show={open} onHide={handleClose} centered>
        <Modal.Body className="d-flex flex-column" style={{ height: "100%" }}>
          <form
            className="d-flex flex-column h-100"
            onSubmit={showOtpInput ? verifyOtpHandle : handleContinue}
          >
            {/* Scrollable content */}
            <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
              <h1
                className="text-center fw-bold"
                style={{
                  fontSize: "2rem",
                  // background: 'linear-gradient(45deg, #4b6cb7, #182848)',
                  WebkitBackgroundClip: "text",
                  color: "#113C65",
                }}
              >
                Welcome
              </h1>

              <h4
                className="fw-semibold text-center text-muted mb-4"
                style={{ fontSize: "1rem" }}
              >
                Sign in to continue
              </h4>

              {showOtpInput && (
                <p className="fs-6 text-center text-secondary">
                  Enter the OTP sent to{" "}
                  <span className="fw-bold">{userInput}</span>
                </p>
              )}

              <div className="text-center mt-4">
                {showOtpInput ? (
                  <div
                    className="d-flex justify-content-center gap-2"
                    style={{ maxWidth: "320px", margin: "0 auto" }}
                  >
                    {otp.map((value, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        className="form-control text-center fw-bold fs-4 border rounded-3 shadow-sm"
                        style={{
                          width: "50px",
                          height: "55px",
                          outline: "none",
                          boxShadow: "0 0 0 1px rgba(0,0,0,0.05)",
                        }}
                        type="number"
                        maxLength={1}
                        value={value}
                        onKeyUp={(event) => handleBackspace(index, event)}
                        onChange={(event) => handleChange(index, event)}
                      />
                    ))}
                  </div>
                ) : (
                  <>
                    <input
                      className="form-control rounded-5 py-2 px-3 mt-3 shadow-sm"
                      style={{
                        border: "1px solid #dee2e6",
                        fontSize: "1rem",
                      }}
                      type="text"
                      placeholder="Enter Mobile Number"
                      value={userInput}
                      onChange={(e) => handlePhoneNumberChange(e)}
                    />
                    {!isValidPhoneNumber(userInput) && userInput.length > 0 && (
                      <p className="text-danger small mt-1">
                        Please enter a valid 10-digit phone number.
                      </p>
                    )}
                  </>
                )}

                <p className="small text-muted mt-3">
                  {showOtpInput ? (
                    <>
                      Didn’t get the OTP?
                      <span
                        className="fw-bold text-primary cursor-pointer ms-1"
                        onClick={(e) => handleContinue(e, 2)}
                        style={{ cursor: "pointer" }}
                      >
                        Resend
                      </span>
                    </>
                  ) : (
                    "You’ll receive a verification code via SMS."
                  )}
                </p>

                {/* Google Login */}
                {!showOtpInput && (
                  <>
                    {/* Divider text */}
                    <div className="d-flex align-items-center my-3">
                      <hr className="flex-grow-1" />
                      <span className="mx-2 text-muted small">
                        Or continue with Google
                      </span>
                      <hr className="flex-grow-1" />
                    </div>

                    {/* Google Button */}
                    <div className="mt-2 d-flex justify-content-center">
                      <GoogleOAuthProvider clientId={clientId}>
                        <GoogleLogin
                          onSuccess={handleGoogleLoginSuccess}
                          onError={handleGoogleLoginFailure}
                          width="320"
                          shape="pill"
                          size="large"
                          text="signin_with"
                        />
                      </GoogleOAuthProvider>
                    </div>
                  </>
                )}
                {/* CSC Login */}
                {/* <div className="mt-3 d-flex justify-content-center">
              <button
                type="button"
                onClick={() => {
                  window.location.href = "http://localhost:6002/admin/auth/csc";
                }}
                className="btn w-100 rounded-pill fw-semibold"
                style={{
                  maxWidth: "320px",
                  border: "1px solid #113C65",
                  color: "#113C65",
                  background: "#fff",
                  padding: "10px",
                }}
              >
                Login with CSC
              </button>
            </div> */}
              </div>
            </div>

            {/* Button pinned at bottom */}
            <div className="text-center mt-4">
              <button
                className="btn btn-primary w-100 py-2 rounded-3 fw-semibold shadow-sm"
                type="submit"
                style={{
                  fontSize: "1.1rem",
                  background: "linear-gradient(45deg, #4b6cb7, #182848)",
                  border: "none",
                }}
              >
                {!isLoading ? (
                  <>
                    Continue <FaArrowRight className="ms-1" />
                  </>
                ) : (
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                )}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      {/* REGISTER MODAL */}
      <Modal
        show={showRegisterModal}
        onHide={() => setShowRegisterModal(false)}
      >
        <Modal.Body
          className="px-4 pb-4 d-flex flex-column"
          style={{ height: "100%" }}
        >
          <form
            className="d-flex flex-column h-100 justify-content-between flex-grow-1"
            style={{ height: "100%" }}
            onSubmit={(e) => e.preventDefault()}
          >
            {/* Scrollable content */}
            <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
              <h4 className="fw-bold ff_inter custom_size color_darkgray">
                Sign Up
              </h4>

              <div className="mt-3">
                <label htmlFor="name">Name</label>
                <input
                  className="custom_input rounded-2 py-2 px-3 w-100"
                  type="text"
                  placeholder="Enter Your Name"
                  value={newuserInfo.name}
                  onChange={(e) =>
                    setNewuserInfo({ ...newuserInfo, name: e.target.value })
                  }
                />
              </div>

              <div className="mt-4">
                <label htmlFor="mail">Mail</label>
                <input
                  className="custom_input rounded-2 py-2 px-3 w-100"
                  type="email"
                  placeholder="Enter Your Mail"
                  value={newuserInfo.email}
                  onChange={(e) =>
                    setNewuserInfo({ ...newuserInfo, email: e.target.value })
                  }
                />
              </div>

              <div className="mt-4">
                <label htmlFor="city">City</label>
                <input
                  className="custom_input rounded-2 py-2 px-3 w-100"
                  type="text"
                  placeholder="Enter Your City"
                  value={newuserInfo.city}
                  onChange={(e) =>
                    setNewuserInfo({ ...newuserInfo, city: e.target.value })
                  }
                />
              </div>

              <div className="mt-4">
                <label htmlFor="state">State</label>
                <input
                  className="custom_input rounded-2 py-2 px-3 w-100"
                  type="text"
                  placeholder="Enter Your State"
                  value={newuserInfo.state}
                  onChange={(e) =>
                    setNewuserInfo({ ...newuserInfo, state: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Fixed button at bottom */}
            <div className="mt-auto pt-3">
              <button
                className="btn btn-primary w-100 py-2 rounded-3 fw-semibold shadow-sm"
                onClick={addUser}
                style={{
                  fontSize: "1.1rem",
                  background: "linear-gradient(45deg, #4b6cb7, #182848)",
                  border: "none",
                }}
              >
                {!isLoading ? (
                  <>
                    Continue <FaArrowRight className="ms-1" />
                  </>
                ) : (
                  <Spinner animation="border" role="status" size="sm" />
                )}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LoginModal;
